import * as React from 'react'
import { useState, useEffect } from 'react'
import clx from 'classnames'

// import { ChevronDownIcon } from '../icons/ChevronDown'
// import { CheckIcon } from '../icons/Check'
import { useClickOutside } from '@/app/hooks/useClickOutside'

import { CheckIcon } from '@/app/features/icons/Check'
import { FaIcon } from '@/lib/fa'
import { useLocalizeSafe } from '@/modules/shared/context/localize/LocalizeContext'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
export type Option = {
  key?: string | number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
  sublabel?: string
}

export type DataSource = {
  id: string
  label: string
  options: Array<Option>
}

type Props = {
  background?: boolean
  label?: string
  placeholder?: string
  icon?: React.ElementType
  options?: Array<Option>
  sources?: Array<DataSource>
  className?: string
  value?: string | number | null
  error?: string | boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
  onSourceChange?: (sourceId: string) => void
  handleChange?: (
    value: string,
    props: { name: string; option: Option },
  ) => void
  name?: string
  disabled?: boolean
  children?: React.ReactNode
  required?: boolean
  overflow?: boolean
  caretSize?: 'xs' | 'sm' | 'md'
  optionAlign?: 'center' | 'start'
  valueClassName?: string
  searchEnabled?: boolean
}

export const FieldsetMultiSourceSelect = ({
  label,
  value,
  options: singleOptions,
  sources,
  name,
  overflow = false,
  placeholder,
  icon: Icon = null,
  disabled,
  handleChange,
  onChange,
  onSourceChange,
  error,
  className,
  required,
  children,
  caretSize = 'md',
  optionAlign = 'center',
  valueClassName = '',
  searchEnabled = true,
  background = false,
}: Props) => {
  const { t } = useLocalizeSafe()
  const resolvedPlaceholder = placeholder ?? t('common.select')
  const [storedValue, setStoredValue] = useState<string | number>(value)
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [openUpward, setOpenUpward] = useState(false)
  const [activeSourceId, setActiveSourceId] = useState<string>(
    sources?.[0]?.id || '',
  )

  // Determine which options to use
  const currentOptions = sources
    ? sources.find((s) => s.id === activeSourceId)?.options || []
    : singleOptions || []

  const selected = currentOptions?.find((o) => o.value === value) || null
  const inputRef = React.useRef<HTMLInputElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const toggle = () => {
    if (!disabled) {
      if (!open && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const dropdownHeight = 250

        setOpenUpward(spaceBelow < dropdownHeight && rect.top > dropdownHeight)
      }

      setOpen(!open)
      if (!open) {
        setSearchQuery('')
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    }
  }

  const select = (option: Option) => {
    setOpen(false)
    setStoredValue(option.value)
    setSearchQuery('')

    if (handleChange) handleChange(option.value, { name, option })

    if (onChange) {
      const event = {
        target: {
          name,
          value: option.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onChange(event)
    }
  }

  const filteredOptions = currentOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  useEffect(() => {
    if (value !== undefined && value !== storedValue) {
      setStoredValue(value)
    }
  }, [value])

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  useClickOutside({
    ref: dropdownRef,
    handler: () => {
      setOpen(false)
      setSearchQuery('')
    },
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key)) {
        setOpen(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % filteredOptions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(
        (prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length,
      )
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0) select(filteredOptions[focusedIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setSearchQuery('')
    }
  }

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as HTMLElement
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  useEffect(() => {
    if (searchQuery && filteredOptions.length > 0) {
      setFocusedIndex(0)
    }
  }, [searchQuery, filteredOptions.length])

  return (
    <div className={className}>
      <input
        type="hidden"
        required={required}
        name={name}
        value={storedValue}
        readOnly
      />
      {label && (
        <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}
      <div
        className={clx('tw-relative tw-w-full')}
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="dropdown-list"
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={toggle}
          className={clx(
            'tw-flex tw-h-9 tw-w-full tw-justify-between tw-gap-2 tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-left tw-transition-all focus:tw-ring-2 focus:tw-ring-focus-100 focus:tw-ring-offset-2',
            {
              'tw-items-center': optionAlign === 'center',
              'tw-items-start': optionAlign === 'start',
            },
            !open && 'hover:tw-opacity-80',
            overflow && 'tw-overflow-auto',
            background && 'tw-bg-white',
            {
              '[&:not(:focus)]:tw-border-error': error,
            },
            valueClassName,
          )}
        >
          <span className="tw-flex tw-items-center tw-gap-1 tw-font-medium">
            {Icon && (
              <span className="tw-p-0.5">
                <Icon className="tw-h-4 tw-w-4" />
              </span>
            )}
            {selected ? (
              selected.label
            ) : (
              <span className="tw-text-sm tw-font-normal tw-text-input">
                {resolvedPlaceholder}
              </span>
            )}
          </span>
          <FaIcon
            icon={faChevronDown}
            className={clx(
              { 'tw-h-3 tw-w-3': caretSize === 'xs' },
              { 'tw-h-4 tw-w-4': caretSize === 'sm' },
              { 'tw-h-5 tw-w-5': caretSize === 'md' },
              `tw-transition-transform`,
              { 'tw-rotate-180': open },
            )}
          />
        </button>

        {open && (
          <div
            className={clx(
              'tw-absolute tw-z-[500] tw-w-full tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-shadow-multi',
              openUpward ? 'tw-bottom-full tw-mb-2' : 'tw-mt-2',
            )}
          >
            {/* Source tabs */}
            {sources && sources.length > 1 && (
              <div className="tw-w-full tw-border-b tw-botder-gray-300">
                {sources.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => {
                      setActiveSourceId(source.id)
                      setSearchQuery('')
                      setFocusedIndex(-1)
                      if (onSourceChange) {
                        onSourceChange(source.id)
                      }
                    }}
                    className={clx(
                      'tw-flex-1 tw-px-6 tw-w-1/2 tw-py-3 tw-text-sm tw-font-medium ',
                      activeSourceId === source.id
                        ? 'tw-text-primary tw-border-b-2 tw-border-primary'
                        : ' tw-text-content-base',
                    )}
                  >
                    {source.label}
                  </button>
                ))}
              </div>
            )}

            {searchEnabled && (
              <div className="tw-px-2 tw-pt-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('common.search')}
                  className="tw-w-full tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-px-3 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-focus-100"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <ul
              ref={listRef}
              className="custom-scrollbar hide-scrollbar tw-flex tw-max-h-[200px] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-px-1.5 tw-py-1.5 tw-text-content-base"
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, idx) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => select(option)}
                      className={clx(
                        'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-[4px] tw-px-1 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-300 hover:tw-bg-opacity-80 focus:tw-bg-base-300 focus:tw-bg-opacity-80 focus:tw-outline-none',
                        selected?.value === option.value &&
                          '!tw-bg-secondary tw-bg-opacity-80 tw-font-medium',
                        focusedIndex === idx &&
                          'tw-bg-base-300 tw-bg-opacity-80',
                      )}
                    >
                      <span className="tw-flex tw-items-center tw-gap-1">
                        {option.icon && (
                          <span className="tw-p-1">
                            <option.icon className="tw-h-3" />
                          </span>
                        )}
                        <span className="tw-flex tw-flex-col tw-text-left">
                          <span>
                            {option.content || option.label || option.value}
                          </span>
                          {option.sublabel && (
                            <span className="tw-text-xs tw-text-gray-500">
                              {option.sublabel}
                            </span>
                          )}
                        </span>
                      </span>
                      {selected?.value === option.value && (
                        <CheckIcon className="tw-h-5 tw-w-5 tw-text-focus-100" />
                      )}
                    </button>
                  </li>
                ))
              ) : (
                <li className="tw-px-1 tw-py-2 tw-text-center tw-text-sm tw-text-gray-500">
                  {t('common.no-results')}
                </li>
              )}
            </ul>
          </div>
        )}
        {name && <input type="hidden" name={name} value={value ?? ''} />}
      </div>
      {error && typeof error === 'string' && (
        <span className="tw-group-focus:tw-opacity-0 tw-text-xs tw-font-medium tw-text-error">
          {error}
        </span>
      )}
      <div>{children}</div>
    </div>
  )
}
